import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ChevronDown,
  CircleHelp,
  ExternalLink,
  MonitorCog,
  RotateCcw,
  Search,
  ShieldAlert,
  Wrench,
} from "lucide-react";
import { SeoMeta, SiteFooter, SiteNavbar } from "../shared/site";
import "../index.css";

const FAQS = [
  {
    category: "Before buying",
    icon: CircleHelp,
    question: "What do I need to know before buying?",
    answer: (
      <>
        <p>
          Products are tested on original Windows builds. Avoid repacks, heavily modified systems, and cut-down
          Windows versions. Original does not mean activated; it means installed from the official Microsoft image.
        </p>
        <p>
          You may need to change system settings before using a product. Support can guide you, but basic PC access
          and patience are required.
        </p>
        <p>
          In the worst case, a clean Windows reinstall can be required. Be ready for that before purchasing.
        </p>
        <a
          href="https://go.microsoft.com/fwlink/?LinkId=691209"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 font-bold"
        >
          Download Windows
          <ExternalLink size={16} />
        </a>
      </>
    ),
  },
  {
    category: "Setup",
    icon: Wrench,
    question: "How do I prepare my computer before using a product?",
    answer: (
      <>
        <p>
          Before launch, prepare Windows and follow the setup instructions carefully. Most launch issues come from
          missing preparation, unsupported Windows builds, or security settings left enabled.
        </p>
        <a
          href="https://hackseller.gitbook.io/hackseller/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 font-bold"
        >
          Open preparation manual
          <ExternalLink size={16} />
        </a>
      </>
    ),
  },
  {
    category: "Spoofer",
    icon: MonitorCog,
    question: "What is a spoofer and when is it useful?",
    answer: (
      <>
        <p>
          A spoofer changes hardware identifiers seen by some systems. It does not recover a banned game account,
          but it may be useful when a game blocks new accounts on the same machine.
        </p>
        <p>
          Use it only when you understand what problem you are trying to solve. For normal product setup, a spoofer
          is not always required.
        </p>
        <Link to="/Spoofer" className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 font-bold">
          View spoofer page
        </Link>
      </>
    ),
  },
  {
    category: "Ban",
    icon: ShieldAlert,
    question: "I got banned. What can I do?",
    answer: (
      <>
        <p>
          Do not panic. This is a normal practice for users who rotate in the world of cheats. First, identify whether
          it is an account ban, an IP issue, or a possible hardware-related ban. Do not keep testing repeatedly on
          valuable accounts.
        </p>
        <ol className="list-decimal space-y-2 pl-6">
          <li>Reboot your router 3-5 times, with a pause of about 1 minute between each reboot.</li>
          <li>
            Ideally, reinstall Windows so the system is fully cleaned and bindings are reset.{" "}
            <a
              href="https://go.microsoft.com/fwlink/?LinkId=691209"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-300 hover:text-purple-200 font-bold"
            >
              Download Windows
            </a>
          </li>
          <li>
            Use a spoofer to replace the serial numbers of your computer components.{" "}
            <Link to="/Spoofer" className="text-purple-300 hover:text-purple-200 font-bold">
              Buy SPOOFER
            </Link>
          </li>
          <li>Launch the game with a new account only after the previous steps have been completed.</li>
        </ol>
        <div>
          <p className="font-extrabold text-white">If you do not want to reinstall Windows:</p>
          <ul className="mt-2 list-disc space-y-2 pl-6">
            <li>Uninstall the game and its launcher.</li>
            <li>
              Download and run these two .bat files as administrator to clean your system:{" "}
              <a
                href="https://mega.nz/folder/HR4AAYhT#j131Dc93pco5JWTMECdxfA"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-300 hover:text-purple-200 font-bold"
              >
                DOWNLOAD
              </a>
            </li>
            <li>For more confidence, use CCleaner and clean your PC completely.</li>
          </ul>
        </div>
      </>
    ),
  },
  {
    category: "Ban",
    icon: AlertTriangle,
    question: "What is a hardware ban?",
    answer: (
      <>
        <p>
          A hardware ban means the restriction may be linked to your computer components or identifiers, not only to
          the game account.
        </p>
        <p>
          It is usually difficult to know the exact component involved. The practical test is whether fresh accounts
          keep getting restricted quickly on the same machine.
        </p>
      </>
    ),
  },
  {
    category: "Payment",
    icon: CircleHelp,
    question: "How fast is delivery after payment?",
    answer: (
      <>
        <p>
          Delivery is usually instant or very fast after payment confirmation. Some products can require manual
          support delivery, especially when setup instructions or compatibility checks are needed.
        </p>
        <p>
          If you do not receive access quickly, open a Discord ticket with your order details.
        </p>
      </>
    ),
  },
  {
    category: "Payment",
    icon: RotateCcw,
    question: "Can I get a refund if I change my mind?",
    answer: (
      <>
        <p>
          Refunds are not provided for simple change of mind, wrong product choice, skipped instructions, or products
          temporarily marked as updating.
        </p>
        <p>
          Refunds are reviewed when the store cannot provide the paid product or when there is a clear delivery issue
          caused by the service.
        </p>
        <Link to="/Delivery" className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 font-bold">
          Read delivery and refund policy
        </Link>
      </>
    ),
  },
  {
    category: "Status",
    icon: ShieldAlert,
    question: "What does Updating or Frozen mean?",
    answer: (
      <>
        <p>
          Updating means the product is being adjusted or checked after a game or anti-cheat change. Frozen means the
          product is intentionally paused and should not be purchased or launched until support confirms availability.
        </p>
        <p>
          Always check the live status before buying or launching a product.
        </p>
        <Link to="/Status" className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 font-bold">
          Open live status
        </Link>
      </>
    ),
  },
  {
    category: "Setup",
    icon: Wrench,
    question: "Which Windows versions are supported?",
    answer: (
      <>
        <p>
          Support depends on the product. Most product pages list Windows 10 and Windows 11 compatibility plus specific
          builds when needed.
        </p>
        <p>
          Avoid custom stripped Windows installs. If your system is heavily modified, support may ask you to reinstall
          a clean Windows image.
        </p>
      </>
    ),
  },
  {
    category: "Support",
    icon: CircleHelp,
    question: "What should I send when opening a support ticket?",
    answer: (
      <>
        <p>Send your product name, order ID if you have one, Windows version, screenshots of the issue, and what steps you already tried.</p>
        <p>
          Clear information helps support answer faster and avoids repeated questions.
        </p>
      </>
    ),
  },
  {
    category: "Before buying",
    icon: AlertTriangle,
    question: "Should I buy while a product is updating?",
    answer: (
      <>
        <p>
          No. If a product is marked Updating, Frozen, or Detected, wait or contact support before paying.
        </p>
        <p>
          Buying during maintenance does not make the product become available faster.
        </p>
      </>
    ),
  },
  {
    category: "Setup",
    icon: MonitorCog,
    question: "Do I need to disable antivirus or security settings?",
    answer: (
      <>
        <p>
          Some products require Windows security changes before launch. Follow the product instructions exactly and do
          not guess settings randomly.
        </p>
        <p>
          If you are unsure, ask support before launching the product.
        </p>
      </>
    ),
  },
  {
    category: "Support",
    icon: Wrench,
    question: "Can support set everything up for me?",
    answer: (
      <>
        <p>
          Support can guide you through setup, but you still need access to your PC and must follow the instructions.
          Basic Windows actions may be required.
        </p>
        <p>
          If you refuse required preparation steps, support may not be able to make the product work.
        </p>
      </>
    ),
  },
];

const CATEGORY_GROUPS = {
  All: ["All"],
  Buying: ["Before buying", "Payment", "Status"],
  Setup: ["Setup", "Spoofer"],
  Ban: ["Ban"],
  Support: ["Support"],
};

const CATEGORIES = ["All", ...Array.from(new Set(FAQS.map((faq) => faq.category)))];
const TOPIC_GROUPS = Object.entries(CATEGORY_GROUPS)
  .filter(([group]) => group !== "All")
  .map(([group, categories]) => ({
    group,
    categories,
    items: FAQS.filter((faq) => categories.includes(faq.category)),
  }))
  .filter((group) => group.items.length > 0);

function QuickAction({ icon: Icon, title, text, action }) {
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-lg border border-purple-400/20 bg-gray-950/55 px-3 py-3 shadow-lg">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-purple-500/35 bg-purple-500/10 text-purple-300">
        <Icon size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-lg font-extrabold leading-tight text-white">{title}</h3>
        <p className="truncate text-base text-gray-400">{text}</p>
      </div>
      {action}
    </div>
  );
}

function FaqItem({ faq, index, isOpen, onToggle }) {
  const Icon = faq.icon;

  return (
    <div className="rounded-lg border border-purple-400/20 bg-gray-950/60 shadow-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-4 md:px-6 py-5 text-left hover:bg-gray-800/45 transition"
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${index}`}
      >
        <span className="hidden sm:inline-flex rounded-lg border border-purple-500/40 bg-purple-500/10 p-3 text-purple-300 shrink-0">
          <Icon size={22} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="text-purple-300 text-base font-bold">{faq.category}</span>
          <span className="block text-white text-xl md:text-2xl font-extrabold leading-tight mt-1">
            {faq.question}
          </span>
        </span>
        <ChevronDown
          size={28}
          className={`text-purple-300 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={`faq-answer-${index}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-purple-400/20 px-4 md:px-6 py-5 text-gray-200 text-lg md:text-xl leading-relaxed space-y-4">
              {faq.answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Faq() {
  const [openIndex, setOpenIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredFaqs = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return FAQS.filter((faq) => {
      const categoryGroup = CATEGORY_GROUPS[selectedCategory] || [selectedCategory];
      const matchesCategory = selectedCategory === "All" || categoryGroup.includes(faq.category);
      const matchesSearch =
        !normalizedSearch ||
        faq.question.toLowerCase().includes(normalizedSearch) ||
        faq.category.toLowerCase().includes(normalizedSearch);

      return matchesCategory && matchesSearch;
    });
  }, [searchTerm, selectedCategory]);

  return (
    <div className="relative min-h-screen flex flex-col bg-transparent text-white font-jersey overflow-hidden">
      <SeoMeta
        title="FAQ"
        description="HackSeller FAQ with purchase, setup, Windows preparation, support and delivery information."
        canonicalPath="/faq"
      />
      <div className="relative z-10">
        <SiteNavbar animated={false} />

        <section className="pt-28 px-4 md:px-8 lg:px-10 py-10 bg-purple-950/25 backdrop-blur-[2px] border-b border-purple-400/20 rounded-b-3xl shadow-[0_18px_60px_rgba(124,58,237,0.18)]">
          <div className="max-w-7xl mx-auto space-y-6">
            <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
              <div>
                <p className="mb-2 text-lg font-bold text-purple-300">Support center</p>
                <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
                  Frequently Asked Questions
                </h2>
                <p className="mt-3 max-w-3xl text-lg leading-relaxed text-gray-300">
                  Find the important setup, purchase, and support information before opening a ticket.
                </p>
              </div>

              <div className="rounded-lg border border-purple-500/35 bg-purple-500/10 p-4 shadow-lg">
                <div className="flex items-center gap-3 text-lg font-extrabold text-purple-200">
                  <RotateCcw size={24} />
                  Support hours
                </div>
                <p className="mt-1 text-base leading-relaxed text-gray-200">
                  Technical support is available from 9:00 to 23:00 Lisbon time.
                </p>
                <a
                  href="https://discord.gg/HdZBtMFsWA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-base font-bold text-white transition hover:bg-purple-500"
                >
                  Contact support
                  <ExternalLink size={18} />
                </a>
              </div>
            </div>

            <div className="grid gap-3 lg:grid-cols-3">
              <QuickAction
                icon={Wrench}
                title="Preparation manual"
                text="Setup steps before launch."
                action={
                  <a
                    href="https://hackseller.gitbook.io/hackseller/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 rounded-md border border-purple-300/25 bg-purple-500/10 px-3 py-2 text-base font-bold text-purple-100 hover:bg-purple-500/20"
                  >
                    Open
                  </a>
                }
              />
              <QuickAction
                icon={CircleHelp}
                title="Live product status"
                text="Check before buying."
                action={
                  <Link to="/Status" className="shrink-0 rounded-md border border-purple-300/25 bg-purple-500/10 px-3 py-2 text-base font-bold text-purple-100 hover:bg-purple-500/20">
                    Open
                  </Link>
                }
              />
              <QuickAction
                icon={MonitorCog}
                title="Spoofer info"
                text="Only when required."
                action={
                  <Link to="/Spoofer" className="shrink-0 rounded-md border border-purple-300/25 bg-purple-500/10 px-3 py-2 text-base font-bold text-purple-100 hover:bg-purple-500/20">
                    Open
                  </Link>
                }
              />
            </div>

            <div className="rounded-lg border border-purple-400/20 bg-gray-950/50 p-4 md:p-5 space-y-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={21} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search in FAQ..."
                  style={{ color: "#ffffff", backgroundColor: "rgba(3, 7, 18, 0.9)", WebkitTextFillColor: "#ffffff", caretColor: "#60a5fa" }}
                  className="w-full rounded-lg border border-purple-400/20 py-3 pl-12 pr-4 text-white text-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="flex flex-wrap gap-3">
                {CATEGORIES.map((category) => {
                  const selected = selectedCategory === category;

                  return (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setSelectedCategory(category)}
                      className={`rounded-lg border px-4 py-2 text-lg font-bold transition ${
                        selected
                          ? "border-purple-400 bg-purple-600 text-white"
                          : "border-purple-400/20 bg-gray-950/60 text-gray-300 hover:border-purple-400 hover:text-white"
                      }`}
                    >
                      {category}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid lg:grid-cols-[280px_1fr] gap-6 items-start">
              <aside className="hidden lg:block rounded-lg border border-purple-400/20 bg-gray-950/55 p-5 sticky top-28">
                <h3 className="text-2xl font-extrabold text-white mb-4">Topics</h3>
                <div className="space-y-2">
                  {TOPIC_GROUPS.map((topicGroup) => (
                    <button
                      key={topicGroup.group}
                      type="button"
                      onClick={() => {
                        setSelectedCategory(topicGroup.group);
                        setSearchTerm("");
                        const firstQuestion = FAQS.find((faq) => topicGroup.categories.includes(faq.category));
                        setOpenIndex(firstQuestion ? FAQS.indexOf(firstQuestion) : null);
                      }}
                      className={`w-full rounded-lg border px-3 py-3 text-left transition ${
                        selectedCategory === topicGroup.group
                          ? "border-purple-300/55 bg-purple-600/35 text-white"
                          : "border-purple-400/15 bg-gray-950/55 text-gray-300 hover:border-purple-300/45 hover:text-white"
                      }`}
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span className="text-lg font-extrabold">{topicGroup.group}</span>
                        <span className="rounded-md border border-purple-300/20 bg-black/25 px-2 py-0.5 text-sm font-bold text-purple-100">
                          {topicGroup.items.length}
                        </span>
                      </span>
                      <span className="mt-1 block truncate text-sm font-bold text-gray-400">
                        {topicGroup.categories.join(", ")}
                      </span>
                    </button>
                  ))}
                </div>
              </aside>

              <div className="space-y-4">
                {filteredFaqs.length > 0 ? (
                  filteredFaqs.map((faq) => {
                    const originalIndex = FAQS.indexOf(faq);

                    return (
                      <FaqItem
                        key={faq.question}
                        faq={faq}
                        index={originalIndex}
                        isOpen={openIndex === originalIndex}
                        onToggle={() => setOpenIndex(openIndex === originalIndex ? null : originalIndex)}
                      />
                    );
                  })
                ) : (
                  <div className="rounded-lg border border-purple-400/20 bg-gray-950/60 p-8 text-center">
                    <p className="text-2xl font-extrabold text-white">No FAQ result found.</p>
                    <p className="text-gray-300 text-lg mt-2">Try another search or category.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <SiteFooter />
      </div>
    </div>
  );
}
